<template>
  <div class="map-stats-wrapper">
    <v-row class="map-stats-container">
    <!-- Incidencias Totales -->
    <v-col cols="12" sm="6" md="3">
      <v-card
        class="text-center pa-4 stat-card"
        elevation="4"
        :class="{ 'loading': loading }"
      >
        <div class="stat-icon-container mb-2">
          <v-avatar size="56" color="red-lighten-5">
            <v-icon size="32" color="red">mdi-alert-circle</v-icon>
          </v-avatar>
        </div>

        <div class="stat-content">
          <div class="stat-number">
            <template v-if="!loading">
              <span v-if="isMeasured(stats.totalIncidents)">{{ stats.totalIncidents }}</span>
              <span v-else class="stat-absent">Sin datos</span>
            </template>
            <v-skeleton-loader v-else type="text" class="mx-auto" style="width: 60px;"></v-skeleton-loader>
          </div>
          <div class="stat-label">Incidencias cargadas</div>
          <!--
            P13-V5A-R5 · The sublabel used to read `stats.criticalIncidents || 0`, so an ABSENT
            value printed "0 críticas". The /maps parent (views/maps/InteractiveMap.vue) never
            passes this field at all, so that card always claimed zero critical incidents.
            The count itself is real where it IS passed — incidents fetched by mapService whose
            priority is CRITICA — so it is kept and an absent value now says so explicitly.
          -->
          <div class="stat-sublabel">
            <span v-if="isMeasured(stats.criticalIncidents)" class="text-error">
              {{ stats.criticalIncidents }} críticas
            </span>
            <span v-else class="text-medium-emphasis">Críticas: N/D</span>
          </div>
        </div>

        <!--
          P13-V5A-R5 · RETIRED. A trend chip claimed "-5% vs ayer" / "+12% críticas" / "Estable".
          The string was picked from a literal set by `getIncidentsTrend()` based on the CURRENT
          critical count; there is no yesterday anywhere in the map data path — mapService fetches
          only the present snapshot, and nothing stores or reads a prior period. The percentages
          were never computed from two measurements. No producer exists for a comparison, so the
          chip is removed rather than filled.
        -->
      </v-card>
    </v-col>

    <!-- Supervisores Activos -->
    <v-col cols="12" sm="6" md="3">
      <v-card
        class="text-center pa-4 stat-card"
        elevation="4"
        :class="{ 'loading': loading }"
      >
        <div class="stat-icon-container mb-2">
          <v-avatar size="56" color="blue-lighten-5">
            <v-icon size="32" color="blue">mdi-account-supervisor</v-icon>
          </v-avatar>
        </div>

        <div class="stat-content">
          <div class="stat-number">
            <template v-if="!loading">
              <span v-if="isMeasured(stats.activeSupervisors)">{{ stats.activeSupervisors }}</span>
              <span v-else class="stat-absent">Sin datos</span>
            </template>
            <v-skeleton-loader v-else type="text" class="mx-auto" style="width: 60px;"></v-skeleton-loader>
          </div>
          <div class="stat-label">Supervisores activos</div>
          <div class="stat-sublabel">
            <span v-if="isMeasured(stats.totalSupervisors)">de {{ stats.totalSupervisors }} cargados</span>
            <span v-else class="text-medium-emphasis">Total: N/D</span>
          </div>
        </div>

        <!--
          P13-V5A-R5 · The availability bar previously fell back to 0 whenever `totalSupervisors`
          was absent or zero, so an UNMEASURED ratio rendered as a red "0% disponibilidad" bar.
          The ratio is a genuine derivation when both counts are present (supervisors fetched with
          status ACTIVO/EN_RUTA over supervisors fetched), so it is kept for that case only; with
          no denominator there is no percentage and no bar is drawn.
        -->
        <div class="availability-indicator mt-2">
          <template v-if="supervisorAvailabilityPercentage !== null">
            <v-progress-linear
              :model-value="supervisorAvailabilityPercentage"
              :color="getSupervisorAvailabilityColor()"
              height="6"
              rounded
            ></v-progress-linear>
            <span class="text-caption text-grey-darken-1 mt-1">
              {{ supervisorAvailabilityPercentage }}% de los supervisores cargados
            </span>
          </template>
          <span v-else class="text-caption text-medium-emphasis">
            Disponibilidad: sin datos
          </span>
        </div>
      </v-card>
    </v-col>

    <!-- Agencias Operativas -->
    <v-col cols="12" sm="6" md="3">
      <v-card
        class="text-center pa-4 stat-card"
        elevation="4"
        :class="{ 'loading': loading }"
      >
        <div class="stat-icon-container mb-2">
          <v-avatar size="56" color="green-lighten-5">
            <v-icon size="32" color="green">mdi-store</v-icon>
          </v-avatar>
        </div>

        <div class="stat-content">
          <div class="stat-number">
            <template v-if="!loading">
              <span v-if="isMeasured(stats.totalAgencies)">{{ stats.totalAgencies }}</span>
              <span v-else class="stat-absent">Sin datos</span>
            </template>
            <v-skeleton-loader v-else type="text" class="mx-auto" style="width: 60px;"></v-skeleton-loader>
          </div>
          <div class="stat-label">Agencias cargadas</div>
          <div class="stat-sublabel">
            <span v-if="isMeasured(stats.activeAgencies)" class="text-success">
              {{ stats.activeAgencies }} activas
            </span>
            <span v-else class="text-medium-emphasis">Activas: N/D</span>
          </div>
        </div>

        <!--
          P13-V5A-R5 · RETIRED. A "distribución por zonas" strip printed chips reading
          "Norte: n", "Sur: n", "Este: n", where every count was invented arithmetic on the agency
          total (`totalAgencies * 0.3`, `* 0.4`, `* 0.3`) and the three zone names were literals.
          No agency is ever assigned to those zones by any read: the fetched agency carries its own
          `zone` field, which this split ignored entirely. A fabricated breakdown of a real total is
          still a fabrication, so the strip is removed rather than recomputed.
        -->
      </v-card>
    </v-col>

    <!-- Rutas Activas -->
    <v-col cols="12" sm="6" md="3">
      <v-card
        class="text-center pa-4 stat-card"
        elevation="4"
        :class="{ 'loading': loading }"
      >
        <div class="stat-icon-container mb-2">
          <v-avatar size="56" color="purple-lighten-5">
            <v-icon size="32" color="purple">mdi-routes</v-icon>
          </v-avatar>
        </div>

        <div class="stat-content">
          <div class="stat-number">
            <template v-if="!loading">
              <span v-if="isMeasured(stats.activeRoutes)">{{ stats.activeRoutes }}</span>
              <span v-else class="stat-absent">Sin datos</span>
            </template>
            <v-skeleton-loader v-else type="text" class="mx-auto" style="width: 60px;"></v-skeleton-loader>
          </div>
          <div class="stat-label">Rutas activas</div>
          <!--
            P13-V5A-R5 · The sublabel said "N completadas hoy". The count is real (routes fetched
            whose status is COMPLETADA) but NO date filter is applied anywhere in the data path,
            so "hoy" was a claim the number does not support. The count is kept; the false temporal
            qualifier is dropped.
          -->
          <div class="stat-sublabel">
            <span v-if="isMeasured(stats.completedRoutes)" class="text-info">
              {{ stats.completedRoutes }} completadas
            </span>
            <span v-else class="text-medium-emphasis">Completadas: N/D</span>
          </div>
        </div>

        <!--
          P13-V5A-R5 · RETIRED. An "eficiencia" gauge printed "92% eficiencia" with a speedometer
          icon whose colour was thresholded off that same number. `routeEfficiency` was a literal
          `ref(92)` that nothing ever wrote to; no endpoint in mapService produces a route
          efficiency. A gauge with no producer is removed, not defaulted.
        -->
      </v-card>
    </v-col>
  </v-row>

  <!-- Panel de métricas en tiempo real -->
  <v-card class="mt-4 detailed-metrics-card" elevation="2">
    <v-card-title class="bg-blue-grey-lighten-5 py-2">
      <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
      <span class="text-subtitle-1">Métricas en Tiempo Real</span>
    </v-card-title>

    <!--
      P13-V5A-R5 · RETIRED. This panel published three business facts and three trends, none of
      which had a producer. "Tiempo Promedio de Respuesta" was `ref(24)`, "Cobertura Geográfica"
      was `ref(87)` and "Alertas Pendientes" was `ref(5)` — literals that nothing ever assigned to,
      on a panel titled "en Tiempo Real". The trends beneath them ("-8% vs ayer", "Sin cambios",
      "+2 desde ayer") were hard-coded strings with matching arrows; there is no prior period in
      the map data path to compare against. The control that revealed the panel
      ("Ver Métricas Detalladas") named a capability the product does not have and is removed with
      it. The four counts above are NOT withdrawn: they are real counts of what mapService fetched.
    -->
    <v-card-text data-testid="map-stats-realtime-metrics-retired">
      <v-alert type="info" variant="tonal" density="compact">
        Estas métricas no tienen un productor canónico y fueron retiradas en P13-V5A-R5.
        El tiempo de respuesta, la cobertura geográfica y las alertas pendientes no existen como
        capacidad medida en este sistema. Las cifras que se muestran arriba sí son reales: cuentan
        los elementos efectivamente cargados en el mapa.
      </v-alert>
    </v-card-text>
  </v-card>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// Props
interface MapStats {
  totalIncidents: number
  criticalIncidents?: number
  activeSupervisors: number
  totalSupervisors?: number
  totalAgencies: number
  activeAgencies?: number
  activeRoutes: number
  completedRoutes?: number
}

interface Props {
  stats: MapStats
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
})

// P13-V5A-R5: un valor ausente NO es cero. Sólo un número finito es una medición; el 0 real
// se sigue mostrando como 0.
const isMeasured = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value)

// Computed properties
// Derivación real: supervisores cargados con estado ACTIVO/EN_RUTA sobre el total de
// supervisores cargados. Sin denominador medido no hay porcentaje (null, no 0).
const supervisorAvailabilityPercentage = computed<number | null>(() => {
  const active = props.stats.activeSupervisors
  const total = props.stats.totalSupervisors
  if (!isMeasured(active) || !isMeasured(total) || total <= 0) return null
  return Math.round((active / total) * 100)
})

const getSupervisorAvailabilityColor = () => {
  const percentage = supervisorAvailabilityPercentage.value
  if (percentage === null) return 'grey'
  if (percentage >= 80) return 'success'
  if (percentage >= 60) return 'warning'
  return 'error'
}
</script>

<style scoped>
.map-stats-container {
  margin-bottom: 0;
}

.stat-card {
  height: 100%;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.stat-card.loading {
  opacity: 0.7;
}

.stat-icon-container {
  position: relative;
}

.stat-content {
  position: relative;
  z-index: 1;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  color: #1976d2;
  line-height: 1;
  margin-bottom: 4px;
}

/* Ausencia explícita: no se pinta como una cifra */
.stat-absent {
  font-size: 1rem;
  font-weight: 500;
  color: #757575;
}

.stat-label {
  font-size: 0.875rem;
  color: #424242;
  font-weight: 500;
  margin-bottom: 4px;
}

.stat-sublabel {
  font-size: 0.75rem;
  color: #666;
}

.availability-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.detailed-metrics-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Animaciones */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}

.stat-card:hover .stat-icon-container {
  animation: pulse 2s infinite;
}

/* Responsive design */
@media (max-width: 768px) {
  .stat-number {
    font-size: 1.5rem;
  }

  .stat-label {
    font-size: 0.8rem;
  }
}
</style>
